import { Row, Col, Button, Modal, Space } from "antd";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  DollarOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { BiFilterAlt } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../shared/types/common";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import { getAccountsList } from "../../core/store/reducers/accounts.slice";
import {
  deleteForexById,
  getForexList,
  requestGetByModuleCommon,
} from "../../core/store/reducers/master.slice";
import {
  accessAllowed,
  formatAmount,
  formatDate,
  formatMonthYear,
} from "../../shared/utils/utils";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import { useForm } from "antd/lib/form/Form";
import CreateEditForexComponent from "./components/create-edit-forex.component";
import { allPermissions } from "../../shared/constants/constants";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";
import { ROUTE_MANAGE_SALES } from "../../shared/routes/private.routes";

const FOREX_FILTER_LABELS: Record<string, string> = {
  fromCurrencyId: "From Currency",
  toCurrencyId: "To Currency",
};

function forexFilterValueText(key: string, value: any, currencies: any[]): string {
  if (!["fromCurrencyId", "toCurrencyId"].includes(key)) return String(value);
  const ids = Array.isArray(value) ? value : [value];
  return ids
    .map((id: string) => currencies.find((c) => c.id === id)?.name ?? id)
    .join(", ");
}

const FOREX_FILTER_CLEAR: Record<string, any> = {
  fromCurrencyId: [],
  toCurrencyId: [],
};

const ManageForexPage: React.FC = () => {
  const dispatch = useDispatch();
  const { loadingForex, forexList, pagination } = useSelector(
    (state: RootState) => state.master
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [form] = useForm();
  const { screenType } = useSelector((state: RootState) => state.settings);
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null as any);
  const [filterVisible, setFilterVisible] = useState(false);
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "" });
  const [filters, setFilters] = useState({} as any);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(TABLE_ROWS_COUNT);
  const openedFromSales = useRef(false);
  const salesReturn = new URLSearchParams(window.location.search).get("returnToSales") === "1";
  const requestedRate = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const month = params.get("month");
    if (!salesReturn || !params.get("from") || !params.get("to") || !month || !/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return null;
    return { fromCurrencyId: params.get("from")!, toCurrencyId: params.get("to")!, month };
  }, [salesReturn]);

  useEffect(() => {
    if (openedFromSales.current || !requestedRate || !currencies.length || !accessAllowed(myPermissions, allPermissions.CREATE_FOREX)) return;
    if (!currencies.some((currency) => currency.id === requestedRate.fromCurrencyId)
      || !currencies.some((currency) => currency.id === requestedRate.toCurrencyId)) return;
    openedFromSales.current = true;
    setSelectedEntity(null);
    setCreateEditOpen(true);
  }, [requestedRate, currencies, myPermissions]);

  console.log(forexList);

  useEffect(() => {
    loadList();
  }, [filters, sorter, pageSize]);

  const loadList = (page: any = null) => {
    const data = {
      page: page ? page : pagination?.page ? pagination?.page : 1,
      limit: pageSize,
      ...sorter,
    };
    let filter = {} as any;
    Object.keys(filters)?.map((val: any) => {
      if (filters[val]) filter[val] = filters[val];
    });
    dispatch(getForexList({ params: data, body: filter }));
  };

  const createEntityListner = () => {
    setCreateEditOpen(true);
    setSelectedEntity(null);
  };
  const editEntityListner = (entity: any) => {
    Modal.confirm({
      title: "Edit Forex configuration",
      content: "Changing the end month or status affects future conversions. Saved Sales values remain unchanged. For new rates, create a new configuration.",
      okText: "Continue",
      onOk: () => { setCreateEditOpen(true); setSelectedEntity(entity); },
    });
  };

  const deleteEntityListner = (entity: any) => {
    Modal.confirm({
      title: "Delete forex rate",
      content: "Are you sure you want to delete this forex rate?",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        const result = await dispatch(deleteForexById(entity.id));
        if (result?.payload?.data?.statusCode === 200) {
          loadList();
        }
      },
    });
  };

  const headerButtons = (
    <Space size="middle" className="app-page-actions">
      { accessAllowed(myPermissions, allPermissions.CREATE_FOREX) && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={createEntityListner}
        >
          <span className="font-size-14">
            New {!screenType?.isPortrait && "Forex"}
          </span>
        </Button>
      )}
      <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() => setFilterVisible(!filterVisible)}
        title={filterVisible ? "Hide filters" : "Show filters"}
      />
    </Space>
  );

  useEffect(() => {
    fetchCurrencies();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await dispatch(
        requestGetByModuleCommon({ module: "master_currency" })
      ).unwrap();
      if (response?.success) {
        const currencylist = response?.data.items;
        setCurrencies(currencylist);
      }
    } catch (error) {
      console.error("Failed to fetch currencies:", error);
    }
  };

  const onFilterApply = (values: any) => {
    setFilters(values);
  };

  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((k) => isFilterValueActive(filters[k]))
      .map((key) => ({
        key,
        fieldLabel: FOREX_FILTER_LABELS[key] || key,
        valueText: forexFilterValueText(key, filters[key], currencies),
      }));
  }, [filters, currencies]);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    form.resetFields();
  }, [form]);

  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const cleared =
        key in FOREX_FILTER_CLEAR ? FOREX_FILTER_CLEAR[key] : undefined;
      form.setFieldsValue({ [key]: cleared });
    },
    [form]
  );

  const onValuesChange = (_value: any, _allValues: any) => {};
  const canUpdateForex = accessAllowed(myPermissions, allPermissions.UPDATE_FOREX);
  const canDeleteForex =
    accessAllowed(myPermissions, allPermissions.DELETE_FOREX) || canUpdateForex;

  const formProps = {
    form: form,
    name: "manage-project-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "fromCurrencyId",
        label: "",
        placeholder: "From Currency",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: currencies?.map((Currency: any) => ({
          value: Currency?.id,
          label: Currency?.name,
        })),
      },
      {
        name: "toCurrencyId",
        label: "",
        placeholder: "To Currency",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: currencies?.map((Currency: any) => ({
          value: Currency?.id,
          label: Currency?.name,
        })),
      },
    ],
    initialValues: {
      name: "",
    },
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
        onClick: clearAllFilters,
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
    onValuesChange: onValuesChange,
  };

  const columns = [
    {
      title: "From Currency",
      dataIndex: "fromCurrencyName",
      key: "fromCurrencyName",
    },
    {
      title: "To Currency",
      dataIndex: "toCurrencyName",
      key: "toCurrencyName",
    },
    {
      title: "From Month",
      dataIndex: "fromMonth",
      key: "fromMonth",
      render: (value: string) => formatMonthYear(value),
    },
    {
      title: "To Month",
      dataIndex: "toMonth",
      key: "toMonth",
      render: (value: string) => (value ? formatMonthYear(value) : ""),
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      render: (rate: number, record: any) => record.targets?.length
        ? <Space direction="vertical" size={2}>{record.targets.map((target: any) => <span key={target.toCurrencyId}>{target.toCurrencyName}: {Number(target.rate).toLocaleString("en-US", { maximumFractionDigits: 6 })}</span>)}</Space>
        : (rate ? Number(rate).toLocaleString("en-US", { maximumFractionDigits: 6 }) : ""),
    },
    { title: "Status", dataIndex: "status", key: "status", render: (status: string) => status === "INACTIVE" ? "Inactive" : "Active" },
    ...(canUpdateForex || canDeleteForex
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            width: 112,
            render: (_text: any, record: any) => (
              <Space>
                {canUpdateForex && (
                  <Button
                    type="link"
                    onClick={() => editEntityListner(record)}
                    icon={<EditOutlined />}
                    title="Edit"
                    aria-label="Edit forex rate"
                  />
                )}
                {canDeleteForex && (
                  <Button
                    type="link"
                    danger
                    onClick={() => deleteEntityListner(record)}
                    icon={<DeleteOutlined />}
                    title="Delete"
                    aria-label="Delete forex rate"
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];
  

  const onChangeHandler = (_pagination: any, _filters: any, _sorter: any) => {
    if (_sorter.order === undefined) {
      setSorter({ sort_by: "", sort_type: "" });
    } else {
      let order = "";
      let columnKey = "";
      if (_sorter.order === "ascend") order = "asc";
      else order = "desc";

      if (_sorter.columnKey === "enabled") {
        columnKey = "status";
      } else if (_sorter.columnKey === "displayName") {
        columnKey = "name";
      } else {
        columnKey = _sorter.columnKey;
      }
      setSorter({ sort_by: columnKey, sort_type: order });
    }
  };

  const onPageChange = (pageNumber: number) => {
    loadList(pageNumber);
  };
  const onPageSizeChange = (_pageNumber: number, selectedPageSize: number) => {
    setPageSize(selectedPageSize);
  };

  const createEditSubmit = () => {
    setCreateEditOpen(false);
    if (salesReturn) {
      window.location.href = `${ROUTE_MANAGE_SALES}?resumeForex=1`;
      return;
    }
    loadList();
  };

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <DollarOutlined />
              </div>
              <h1 className="app-page-title">Manage Forex</h1>
            </div>
            <p className="app-page-subtitle">
              View and manage forex rates
            </p>
          </Col>
          <Col><Space>
            {salesReturn && <Button onClick={() => { window.location.href = `${ROUTE_MANAGE_SALES}?resumeForex=1`; }}>Back to Sales</Button>}
            {headerButtons}
          </Space></Col>
        </Row>
      </div>
      {filterVisible && (
        <div className="app-filter-bar">
          <div className="app-filter-header">
            <div className="app-filter-title">
              <BiFilterAlt style={{ fontSize: 14 }} />
              Filters
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setFilterVisible(false)}
              title="Close filters"
              aria-label="Close filters"
            />
          </div>
          <div className="table-filter-form">
            <GenericFormComponent {...formProps} />
          </div>
        </div>
      )}

      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      <div className="app-table-card">
        <DataTableComponent
          rowKey="id"
          pagination={true}
          loading={loadingForex}
          dataSource={forexList}
          columns={columns}
          onChange={onChangeHandler}
          defaultPage={0}
          currentPage={pagination?.page || 1}
          totalRows={pagination?.total || 0}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSize={pageSize}
        />
      </div>

      {createEditOpen && (
        <CreateEditForexComponent
          onClose={() => setCreateEditOpen(false)}
          visible={createEditOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
          currencies={currencies}
          prefill={requestedRate}
        />
      )}
    </div>
  );
};

export default ManageForexPage;
